import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useTranslation } from '@/hooks/useTranslation';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Menu,
  Bell,
  User,
  Settings,
  MessageCircle,
  LogOut,
  Home,
  ChevronRight,
  X
} from 'lucide-react';
import { useContactRequests } from '@/hooks/mensag/useContacts';

interface MenuMovilProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MenuMovil({ isOpen, onClose }: MenuMovilProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { t } = useTranslation();
  const { data: contactRequests } = useContactRequests();

  const pendingRequests = contactRequests?.filter(r => r.direction === 'received' && r.status === 'pending').length || 0;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const menuItems = [
    {
      id: 'chats',
      label: t('chats', 'Chats'),
      icon: MessageCircle,
      path: '/',
    },
    {
      id: 'profile',
      label: t('profile', 'Perfil'),
      icon: User,
      path: '/profile',
    },
    {
      id: 'settings',
      label: t('settings', 'Configuración'),
      icon: Settings,
      path: '/settings',
    },
  ];

  return (
    <>
      {/* Overlay Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Drawer */}
      <div className={`
        fixed left-0 top-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white">MensaG</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            aria-label="Cerrar menú"
          >
            <ChevronRight className="h-5 w-5 rotate-180" />
          </Button>
        </div>

        {/* User Info Section */}
        {user && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <div className="flex items-center space-x-3">
              <Avatar className="h-12 w-12 ring-2 ring-gray-200 dark:ring-gray-600">
                <AvatarFallback className="bg-blue-500 text-white font-semibold text-base">
                  {user.profile?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {user.profile?.name || 'Usuario'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {user.profile?.about || 'Usuario de MensaG'}
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="text-center py-2 bg-white dark:bg-gray-600 rounded-lg">
                <div className="text-lg font-semibold text-blue-600">0</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">Chats</div>
              </div>
              <div className="text-center py-2 bg-white dark:bg-gray-600 rounded-lg">
                <div className="text-lg font-semibold text-green-600">0</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">Msgs</div>
              </div>
              <div className="text-center py-2 bg-white dark:bg-gray-600 rounded-lg">
                <div className="text-lg font-semibold text-purple-600">0</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">Contacts</div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.path)}
                  className={`
                    w-full flex items-center justify-start h-12 px-4 text-left font-normal rounded-xl transition-colors duration-200
                    ${isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-r-3 border-blue-600 shadow-sm'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}

            {/* Contact Requests */}
            <button
              onClick={() => handleNavigate('/contacts')}
              className="w-full flex items-center justify-start h-12 px-4 text-left font-normal text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white rounded-xl transition-colors duration-200"
            >
              <div className="flex items-center mr-3 relative flex-shrink-0">
                <Bell className="h-5 w-5" />
                {pendingRequests > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                    {pendingRequests > 9 ? '9+' : pendingRequests}
                  </span>
                )}
              </div>
              <span className="truncate">Solicitudes</span>
            </button>
          </div>
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3">
            MensaG v1.0 • Privacidad First
          </p>
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Handle logout
                window.location.reload(); // Simple logout for now
              }}
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 hover:border-red-300"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar sesión
            </Button>
          )}
        </div>
      </div>
    </>
  );
}